import {initialCalculation} from "./initialCalculation";
export {initialCalculation} from "./initialCalculation";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import { fitDimensionSizes } from "./measurementDimensions";
import type { CalculationRequest, CalculationResult, CalculationTemplate, Measurement } from "./types";

export type CalculatorProps = {
  measurement: Measurement;
  setMeasurement: (measurement: Measurement) => void;
  disabled: boolean;
  onSave: (request: CalculationRequest) => void;
  actionLabel?: string;
  onExport?: (request: CalculationRequest) => void;
  initialRequest?: CalculationRequest | null;
  restoreResults?: boolean;
  onRequestChange?: (request: CalculationRequest) => void;
};


export function useMeasurementCalculator({ measurement, initialRequest, restoreResults, onRequestChange }: CalculatorProps) {
  const [request, setRequest] = useState(() => initialCalculation(measurement, initialRequest));
  const [templates, setTemplates] = useState<CalculationTemplate[]>([]);
  const [templateStatus, setTemplateStatus] = useState<"loading" | "ready" | "error">("loading");
  const [results, setResults] = useState<Array<{ request: CalculationRequest; result: CalculationResult }>>([]);
  const [error, setError] = useState("");
  const [calculating, setCalculating] = useState(false);
  const activeRequest = useRef(request);
  const calculation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const changeCallback = useRef(onRequestChange);
  changeCallback.current = onRequestChange;
  activeRequest.current = request;

  const updateRequest = useCallback((next: CalculationRequest | ((current: CalculationRequest) => CalculationRequest)) => {
    calculation.current?.abort();
    setCalculating(false);
    setError("");
    setRequest(current => {
      const value = typeof next === "function" ? next(current) : next;
      activeRequest.current = value;
      return value;
    });
    setResults([]);
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplateStatus("loading"); setError("");
    try {
      const items = await api.calculatorTemplates();
      if (!mounted.current) return;
      setTemplates(items); setTemplateStatus("ready");
    } catch (reason) {
      if (!mounted.current) return;
      setError(reason instanceof Error ? reason.message : "Не удалось загрузить профильные системы.");
      setTemplateStatus("error");
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void loadTemplates();
    return () => { mounted.current = false; calculation.current?.abort(); };
  }, [loadTemplates]);
  useEffect(() => { changeCallback.current?.(request); }, [request]);

  // Only changed calculation inputs invalidate a result. Parent object identity does not.
  useEffect(() => {
    const current = activeRequest.current;
    const next = { ...current, widthMm: measurement.widthMm, heightMm: measurement.heightMm,
      glazing: measurement.glazing, hardware: measurement.hardware, color: measurement.color, comment: measurement.comment,
      sections: current.sections.map((section, index) => ({ ...section, widthMm: fitDimensionSizes(measurement.widthMm, current.sections.map(item => item.widthMm), current.sections.length)[index] })),
      rowHeightsMm: fitDimensionSizes(measurement.heightMm, current.rowHeightsMm, current.rowCount) };
    if (JSON.stringify(next) !== JSON.stringify(current)) updateRequest(next);
  }, [measurement.widthMm, measurement.heightMm, measurement.glazing, measurement.hardware, measurement.color, measurement.comment, updateRequest]);

  const calculate = useCallback(async (source = activeRequest.current, constructionType = measurement.constructionType) => {
    const template = templates.find(item => item.id === source.templateId && item.constructionType === constructionType);
    if (!template) { setError("Выберите профильную систему."); return false; }
    if(template.sourceCalculation?.engine==="alutex-catalogue-window"&&!source.color){setError("Выберите цвет профиля из 1С на первом шаге.");return false;}
    if (!template.technicalReadiness?.ready && !template.sourceCalculation?.available) { setError(template.technicalReadiness?.issues.join(" ") || "Нет подтверждённой технической карты системы. Эскиз можно редактировать без производственного расчёта."); return false; }
    if (source.widthMm <= 0 || source.heightMm <= 0 || source.quantity < 1 || source.sections.some(section => section.widthMm <= 0) || source.rowHeightsMm.some(height => height <= 0)) {
      setError("Укажите положительные размеры конструкции, секций и рядов."); return false;
    }
    calculation.current?.abort();
    const controller = new AbortController(); calculation.current = controller;
    const variant = { ...source, profileSystem: template.profileSystem };
    activeRequest.current = variant; setRequest(variant); setCalculating(true); setError("");
    const snapshot = JSON.stringify(variant);
    try {
      const result = await api.calculate(variant, controller.signal);
      if (controller.signal.aborted || !mounted.current || snapshot !== JSON.stringify(activeRequest.current)) return false;
      setResults([{ request: variant, result }]); return true;
    } catch (reason) {
      if (!controller.signal.aborted && mounted.current) setError(reason instanceof Error ? reason.message : "Не удалось выполнить расчёт.");
      return false;
    } finally {
      if (calculation.current === controller && mounted.current) setCalculating(false);
    }
  }, [templates, measurement.constructionType]);

  // Source-backed calculations update automatically; stale requests are aborted
  // by updateRequest and the result is accepted only for the same input snapshot.
  const signature = JSON.stringify(request);
  useEffect(() => {
    const current = activeRequest.current;
    const template = templates.find(t => t.id === current.templateId);
    const source = template?.sourceCalculation;
    if (!(source?.available || template?.technicalReadiness?.ready) || !current.glazing || !current.color || !current.hardware
      || current.widthMm <= 0 || current.heightMm <= 0 || current.quantity < 1
      || current.sections.some(section => section.widthMm <= 0) || current.rowHeightsMm.some(height => height <= 0)) return;
    const timer = window.setTimeout(() => { void calculate(); }, 600);
    return () => window.clearTimeout(timer);
  }, [signature, templates, calculate]);
  useEffect(() => {
    if(!templates.find(t=>t.id===request.templateId)?.sourceCalculation?.available || !request.color || request.widthMm<=0 || request.heightMm<=0)return;
    const timer=window.setInterval(()=>{void calculate();},10000);
    return()=>window.clearInterval(timer);
  },[signature,templates,calculate]);
  useEffect(()=>{
    if(!templates.some(t=>t.id===request.templateId&&t.sourceCalculation?.available))return;
    let active=true;let timer:ReturnType<typeof setTimeout>;
    async function refresh(){try{const next=await api.calculatorTemplates();if(active)setTemplates(current=>JSON.stringify(current)===JSON.stringify(next)?current:next);}catch{/* Calculation refresh reports source failures; keep the current form usable. */}finally{if(active)timer=setTimeout(()=>void refresh(),10000);}}
    timer=setTimeout(()=>void refresh(),10000);return()=>{active=false;clearTimeout(timer);};
  },[request.templateId]);

  const restored = useRef(false);
  const restoreOnMount = useRef(!!restoreResults && !!initialRequest?.templateId);
  useEffect(() => {
    if (!restored.current && restoreOnMount.current && templateStatus === "ready") {
      restored.current = true;
      const template = templates.find(item => item.id === activeRequest.current.templateId);
      if (template?.technicalReadiness?.ready && !template.sourceCalculation?.available) void calculate();
    }
  }, [restoreResults, initialRequest, templateStatus, templates, calculate]);

  return { request, setRequest: updateRequest, templates, templateStatus, loadTemplates, results, error, calculating, calculate };
}
